import React, { useEffect, useState } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Box,
	Grid2,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	TextField,
	Alert,
	Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridFilterModel } from '@mui/x-data-grid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import { JudgesProps } from '../types';
import {
	getContestantsOfAContest,
	resetPasswordOfAContestant,
	suspendUserAccount,
	activateUserAccount,
	banUserAccount,
	unbanUserAccount,
} from '../../../core/contests/services';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

function Contestants({ contestId }: JudgesProps) {
	const [contestants, setContestants] = useState<any[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 50,
	});
	const [filterModel, setFilterModel] = useState<GridFilterModel>({
		items: [],
	});

	const currentUser = useCurrentUser();

	// Reset password dialog state
	const [resetDialogOpen, setResetDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<any>(null);
	const [resetLoading, setResetLoading] = useState(false);
	const [resetResult, setResetResult] = useState<{ username: string; new_password: string } | null>(null);
	const [copySuccess, setCopySuccess] = useState(false);

	// Snackbar state
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');

	const showSnackbar = (message: string) => {
		setSnackbarMessage(message);
		setSnackbarOpen(true);
	};

	const handleSnackbarClose = () => {
		setSnackbarOpen(false);
	};

	const handleResetPasswordClick = (user: any) => {
		setSelectedUser(user);
		setResetResult(null);
		setCopySuccess(false);
		setResetDialogOpen(true);
	};

	const handleResetPasswordConfirm = async () => {
		if (!selectedUser) return;

		setResetLoading(true);
		try {
			const response = await resetPasswordOfAContestant(selectedUser.id);
			setResetResult(response);
		} catch (error: any) {
			console.error('Error resetting password:', error);
			// You can add error handling here if needed
		} finally {
			setResetLoading(false);
		}
	};

	const handleCloseDialog = () => {
		setResetDialogOpen(false);
		setSelectedUser(null);
		setResetResult(null);
		setCopySuccess(false);
	};

	const handleCopyPassword = () => {
		if (resetResult?.new_password) {
			navigator.clipboard.writeText(resetResult.new_password);
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 2000);
		}
	};

	const handleSuspendUser = async (user: any) => {
		try {
			await suspendUserAccount(user.id);
			// Update local state
			setContestants((prev) =>
				prev.map((contestant) => (contestant.id === user.id ? { ...contestant, is_active: false } : contestant)),
			);
			showSnackbar(`User ${user.username} has been suspended`);
		} catch (error: any) {
			console.error('Error suspending user:', error);
			showSnackbar('Failed to suspend user');
		}
	};

	const handleActivateUser = async (user: any) => {
		try {
			await activateUserAccount(user.id);
			// Update local state
			setContestants((prev) =>
				prev.map((contestant) => (contestant.id === user.id ? { ...contestant, is_active: true } : contestant)),
			);
			showSnackbar(`User ${user.username} has been activated`);
		} catch (error: any) {
			console.error('Error activating user:', error);
			showSnackbar('Failed to activate user');
		}
	};

	const handleBanUser = async (user: any) => {
		try {
			await banUserAccount(user.id);
			// Update local state
			setContestants((prev) =>
				prev.map((contestant) =>
					contestant.id === user.id ? { ...contestant, is_banned: true, is_active: false } : contestant,
				),
			);
			showSnackbar(`User ${user.username} has been banned`);
		} catch (error: any) {
			console.error('Error banning user:', error);
			showSnackbar('Failed to ban user');
		}
	};

	const handleUnbanUser = async (user: any) => {
		try {
			await unbanUserAccount(user.id);
			// Update local state
			setContestants((prev) =>
				prev.map((contestant) =>
					contestant.id === user.id ? { ...contestant, is_banned: false, is_active: true } : contestant,
				),
			);
			showSnackbar(`User ${user.username} has been unbanned`);
		} catch (error: any) {
			console.error('Error unbanning user:', error);
			showSnackbar('Failed to unban user');
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: 'Username',
			flex: 1,
			minWidth: 150,
			filterable: true,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			minWidth: 150,
			filterable: false,
			valueGetter: (params: any) => {
				const firstName = params.row.first_name || '';
				const lastName = params.row.last_name || '';
				return `${firstName} ${lastName}`.trim() || '-';
			},
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1.5,
			minWidth: 200,
			filterable: true,
		},
		{
			field: 'institution',
			headerName: 'Institution',
			flex: 1.5,
			minWidth: 250,
			filterable: true,
		},
		{
			field: 'action',
			headerName: 'Action',
			flex: 2,
			minWidth: 380,
			filterable: false,
			sortable: false,
			renderCell: (params) => {
				const isActive = params.row.is_active;
				const isBanned = params.row.is_banned;
				if (!currentUser?.is_staff) {
					return <Typography variant='body2'>No Actions Available</Typography>;
				}
				return (
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', py: 1 }}>
						<Button
							variant='contained'
							color='warning'
							size='small'
							startIcon={<LockResetIcon />}
							onClick={() => handleResetPasswordClick(params.row)}
							sx={{ minWidth: 100 }}
						>
							Reset
						</Button>
						{isActive ? (
							<Button
								variant='outlined'
								color='error'
								size='small'
								startIcon={<BlockIcon />}
								onClick={() => handleSuspendUser(params.row)}
								sx={{ minWidth: 100 }}
							>
								Suspend
							</Button>
						) : (
							<Button
								variant='outlined'
								color='success'
								size='small'
								startIcon={<CheckCircleIcon />}
								onClick={() => handleActivateUser(params.row)}
								sx={{ minWidth: 100 }}
							>
								Enable
							</Button>
						)}
						{isBanned ? (
							<Button
								variant='outlined'
								color='success'
								size='small'
								startIcon={<CheckCircleIcon />}
								onClick={() => handleUnbanUser(params.row)}
								sx={{ minWidth: 100 }}
							>
								Unban
							</Button>
						) : (
							<Button
								variant='outlined'
								color='error'
								size='small'
								startIcon={<GavelIcon />}
								onClick={() => handleBanUser(params.row)}
								sx={{ minWidth: 100 }}
							>
								Ban
							</Button>
						)}
					</Box>
				);
			},
		},
	];

	const fetchContestants = (page: number, pageSize: number, filters?: { [key: string]: string }) => {
		setLoading(true);
		const offset = page * pageSize;

		const params: any = {
			limit: pageSize,
			offset,
		};

		// Apply filters from DataGrid
		if (filters) {
			Object.keys(filters).forEach((key) => {
				if (filters[key]) {
					params[key] = filters[key];
				}
			});
		}

		getContestantsOfAContest(contestId, params)
			.then((response) => {
				setContestants(response.results);
				setTotalCount(response.count);
			})
			.catch((error: any) => {
				console.error('Error fetching contestants:', error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (contestId) {
			// Extract filter values from filterModel
			const filters: { [key: string]: string } = {};
			filterModel.items.forEach((item) => {
				if (item.value) {
					filters[item.field] = item.value;
				}
			});

			fetchContestants(paginationModel.page, paginationModel.pageSize, filters);
		}
	}, [contestId, paginationModel.page, paginationModel.pageSize, filterModel]);

	const handlePaginationChange = (newPaginationModel: GridPaginationModel) => {
		setPaginationModel(newPaginationModel);
	};

	const handleFilterChange = (newFilterModel: GridFilterModel) => {
		setFilterModel(newFilterModel);
		// Reset to first page when filtering
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	return (
		<Box sx={{ width: '100%', padding: 2 }}>
			<Card elevation={3} sx={{ width: '100%' }}>
				<CardContent>
					<Typography variant='h5' gutterBottom>
						Contestants List ({totalCount} total)
					</Typography>

					<Box sx={{ width: '100%', mt: 2 }}>
						<DataGrid
							rows={contestants}
							columns={columns}
							rowCount={totalCount}
							loading={loading}
							pageSizeOptions={[25, 50, 100]}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={handlePaginationChange}
							filterMode='server'
							filterModel={filterModel}
							onFilterModelChange={handleFilterChange}
							disableRowSelectionOnClick
							sx={{
								'& .MuiDataGrid-cell': {
									wordBreak: 'break-word',
								},
							}}
						/>
					</Box>
				</CardContent>
			</Card>

			{/* Reset Password Dialog */}
			<Dialog open={resetDialogOpen} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
				<DialogTitle>{resetResult ? 'Password Reset Successful' : 'Confirm Password Reset'}</DialogTitle>
				<DialogContent>
					{!resetResult ? (
						<Box>
							<Typography>
								Are you sure you want to reset the password for user <strong>{selectedUser?.username}</strong>?
							</Typography>
						</Box>
					) : (
						<Box>
							<Alert severity='success' sx={{ mb: 2 }}>
								Password has been successfully reset for <strong>{resetResult.username}</strong>
							</Alert>

							<TextField
								fullWidth
								label='New Password'
								value={resetResult.new_password}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<IconButton onClick={handleCopyPassword} edge='end'>
											<ContentCopyIcon />
										</IconButton>
									),
								}}
								sx={{ mt: 2 }}
							/>

							{copySuccess && (
								<Alert severity='info' sx={{ mt: 2 }}>
									Password copied to clipboard!
								</Alert>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{!resetResult ? (
						<>
							<Button onClick={handleCloseDialog} color='inherit'>
								Cancel
							</Button>
							<Button onClick={handleResetPasswordConfirm} variant='contained' color='warning' disabled={resetLoading}>
								{resetLoading ? 'Resetting...' : 'Reset Password'}
							</Button>
						</>
					) : (
						<Button onClick={handleCloseDialog} variant='contained' color='primary'>
							Close
						</Button>
					)}
				</DialogActions>
			</Dialog>

			{/* Snackbar for notifications */}
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={3000}
				onClose={handleSnackbarClose}
				message={snackbarMessage}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			/>
		</Box>
	);
}

export default Contestants;
